/**
 * ClubMeetingPlanner
 * Plan clubmeetings vooruit — zelfde formvelden als AgendaPage.
 * Sla op in `calendarEvents` met entityId zodat de meeting voor alle
 * clubleden in hun persoonlijke agenda verschijnt.
 *
 * Rule 1: geen breaking changes — SharedAgendaEvent interface uitgebreid, niet gewijzigd.
 * Rule 3: onSnapshot listener + cleanup.
 * Rule 5: writeBatch voor atomische event + inbox-writes.
 * Rule 8: alle aanraakdoelen ≥ 44px, mobiel-first.
 * Rule 9: geen hardcoded strings of kleuren.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  collection, query, where, orderBy, onSnapshot,
  addDoc, writeBatch, doc, serverTimestamp, Timestamp,
} from 'firebase/firestore';
import { db } from '../../../firebase';
import type { SharedAgendaEvent } from '../../../types/sharedAgenda.types';

// ── Constanten ────────────────────────────────────────────────────────────

const EVENT_TYPES = [
  { value: 'meeting',    label: 'Vergadering' },
  { value: 'networking', label: 'Netwerkbijeenkomst' },
  { value: 'planning',   label: 'Planningssessie' },
  { value: 'call',       label: 'Telefonisch overleg' },
  { value: 'personal',   label: 'Overig' },
] as const;

const REMINDER_OPTIONS = [
  { value: 5,    label: '5 minuten van tevoren' },
  { value: 15,   label: '15 minuten van tevoren' },
  { value: 30,   label: '30 minuten van tevoren' },
  { value: 60,   label: '1 uur van tevoren' },
  { value: 120,  label: '2 uur van tevoren' },
  { value: 1440, label: '1 dag van tevoren' },
  { value: 10080,label: '1 week van tevoren' },
];

const REPEAT_OPTIONS = [
  { value: 'none',      label: 'Geen herhaling' },
  { value: 'weekly',    label: 'Wekelijks' },
  { value: 'biweekly',  label: 'Om de week' },
  { value: 'monthly',   label: 'Maandelijks' },
];

const COLORS = [
  '#3B82F6', '#10B981', '#F59E0B',
  '#EF4444', '#8B5CF6', '#EC4899',
];

const VISIBILITY_OPTIONS = [
  { value: 'members',          label: 'Alle leden' },
  { value: 'organizers-only',  label: 'Organisatoren & Bestuur' },
];

// ── Formulier state type ───────────────────────────────────────────────────

interface MeetingForm {
  title: string;
  description: string;
  startDate: string;
  startTime: string;
  endTime: string;
  type: string;
  location: string;
  meetingUrl: string;
  useJitsi: boolean;
  color: string;
  repeat: string;
  repeatEndDate: string;
  reminderEnabled: boolean;
  reminderMinutesBefore: number;
  visibility: string;
  /** true = direct in agenda zetten (verplicht); false = uitnodiging sturen, lid accepteert zelf */
  autoAdd: boolean;
  /** Host type: geen, lid, of gastspreker */
  hostType: 'none' | 'member' | 'guest_speaker';
  hostUid: string;
  hostName: string;
  hostPhotoURL: string;
  guestSpeakerBio: string;
}

const EMPTY_FORM: MeetingForm = {
  title: '',
  description: '',
  startDate: '',
  startTime: '09:00',
  endTime: '10:00',
  type: 'meeting',
  location: '',
  meetingUrl: '',
  useJitsi: true,
  color: '#3B82F6',
  repeat: 'none',
  repeatEndDate: '',
  reminderEnabled: true,
  reminderMinutesBefore: 60,
  visibility: 'members',
  autoAdd: false,
  hostType: 'none',
  hostUid: '',
  hostName: '',
  hostPhotoURL: '',
  guestSpeakerBio: '',
};

// ── Props ─────────────────────────────────────────────────────────────────

interface ClubMeetingPlannerProps {
  entityId: string;
  clubName: string;
  currentUserId: string;
  currentUserName: string;
  /** UIDs van alle actieve leden — worden als participant aan calendarEvent toegevoegd */
  memberUids: string[];
  /** Volledige ledenlijst voor host-selector (naam + foto) */
  members?: { id: string; name: string; photoURL?: string }[];
  canPlan: boolean;  // owner | organizer | board
  primaryColor?: string;
  accentColor?: string;
  /** Placeholder voor het titel-veld */
  titlePlaceholder?: string;
}

// ── Hulpfuncties ──────────────────────────────────────────────────────────

function formatEventDate(ts: any): string {
  const d = ts?.toDate?.() || (ts instanceof Date ? ts : new Date(ts));
  return new Intl.DateTimeFormat('nl-NL', {
    weekday: 'short', day: 'numeric', month: 'short',
    hour: '2-digit', minute: '2-digit',
  }).format(d);
}

function isUpcoming(ts: any): boolean {
  const d = ts?.toDate?.() || (ts instanceof Date ? ts : new Date(ts));
  return d.getTime() > Date.now();
}

// ── Component ─────────────────────────────────────────────────────────────

const ClubMeetingPlanner: React.FC<ClubMeetingPlannerProps> = ({
  entityId,
  clubName,
  currentUserId,
  currentUserName,
  memberUids,
  members = [],
  canPlan,
  primaryColor = '#2D5E4F',
  accentColor = '#E97F4A',
  titlePlaceholder = 'Bijv. Maandelijkse netwerkbijeenkomst',
}) => {
  const [meetings, setMeetings] = useState<SharedAgendaEvent[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<MeetingForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<false | 'auto' | 'invite'>(false);
  const [locationSuggestions, setLocationSuggestions] = useState<{ display_name: string; lat: string; lon: string }[]>([]);
  const [locationSearching, setLocationSearching] = useState(false);
  const locationDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const searchLocation = useCallback((query: string) => {
    if (locationDebounceRef.current) clearTimeout(locationDebounceRef.current);
    if (!query.trim() || query.trim().length < 3) { setLocationSuggestions([]); return; }
    locationDebounceRef.current = setTimeout(async () => {
      setLocationSearching(true);
      try {
        const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=5&addressdetails=1`, {
          headers: { 'Accept-Language': 'nl' },
        });
        const data = await res.json();
        setLocationSuggestions(data || []);
      } catch { setLocationSuggestions([]); }
      finally { setLocationSearching(false); }
    }, 400);
  }, []);

  // ── Real-time listener voor clubmeetings ──────────────────────────────
  useEffect(() => {
    if (!entityId) return;
    const q = query(
      collection(db, 'calendarEvents'),
      where('entityId', '==', entityId),
      orderBy('startTime', 'asc'),
    );
    const unsub = onSnapshot(q, (snap) => {
      const events = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as SharedAgendaEvent[];
      setMeetings(events);
    }, (err) => {
      // Fallback als index ontbreekt: geen ordering
      console.warn('[ClubMeetingPlanner] onSnapshot error (index?)', err);
    });
    return () => unsub();
  }, [entityId]);

  const set = useCallback(<K extends keyof MeetingForm>(key: K, val: MeetingForm[K]) => {
    setForm(f => ({ ...f, [key]: val }));
    setError(null);
  }, []);

  // ── Opslaan ───────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.title.trim()) { setError('Vul een titel in.'); return; }
    if (!form.startDate)    { setError('Kies een datum.'); return; }
    if (!form.startTime)    { setError('Kies een starttijd.'); return; }

    setSaving(true);
    setError(null);

    try {
      const startDateTime = new Date(`${form.startDate}T${form.startTime}:00`);
      const endDateTime   = new Date(`${form.startDate}T${form.endTime || form.startTime}:00`);
      if (endDateTime <= startDateTime) endDateTime.setHours(endDateTime.getHours() + 1);

      // Bepaal de meeting URL: Jitsi of eigen link
      const resolvedMeetingUrl = form.useJitsi
        ? `https://video.superkonnected.com/${entityId.replace(/[^a-z0-9]/gi, '-')}-${Date.now()}`
        : form.meetingUrl.trim() || undefined;

      const reminderAt = form.reminderEnabled
        ? new Date(startDateTime.getTime() - form.reminderMinutesBefore * 60_000)
        : null;

      // Helper: remove undefined values so Firestore writeBatch doesn't reject
      const clean = <T extends Record<string, any>>(obj: T): T =>
        Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;

      const recurring = form.repeat !== 'none' ? clean({
        frequency: form.repeat as 'daily' | 'weekly' | 'monthly',
        endDate: form.repeatEndDate ? new Date(form.repeatEndDate) : undefined,
      }) : null;

      const batch = writeBatch(db);
      const eventRef = doc(collection(db, 'calendarEvents'));
      const startTs = Timestamp.fromDate(startDateTime);

      // Build host object
      const hostData = form.hostType !== 'none' && (form.hostName || form.hostUid) ? clean({
        type: form.hostType,
        uid: form.hostUid || null,
        name: form.hostName || null,
        photoURL: form.hostPhotoURL || null,
        bio: form.guestSpeakerBio || null,
      }) : null;

      if (form.autoAdd) {
        // ── Verplichte meeting: direct in agenda van alle leden ────────────
        const participants = Array.from(new Set([currentUserId, ...memberUids]));
        const eventData = clean({
          title: form.title.trim(),
          description: form.description.trim() || null,
          startTime: startTs,
          endTime: Timestamp.fromDate(endDateTime),
          type: form.type,
          category: 'work',
          domain: 'work',
          participants,
          participantNames: { [currentUserId]: currentUserName },
          location: form.location.trim() || null,
          meetingUrl: resolvedMeetingUrl || null,
          status: 'scheduled',
          createdBy: currentUserId,
          createdAt: serverTimestamp(),
          color: form.color,
          reminderEnabled: form.reminderEnabled,
          reminderMinutesBefore: form.reminderMinutesBefore,
          reminderAt: reminderAt ? Timestamp.fromDate(reminderAt) : null,
          reminderSent: false,
          recurring: recurring || null,
          entityId,
          entityType: 'networkclub',
          visibility: form.visibility,
          source: 'superkonnected',
          host: hostData,
        });
        batch.set(eventRef, eventData);

        // Inbox-kennisgeving (informatief, geen RSVP nodig)
        memberUids.forEach((uid) => {
          if (uid === currentUserId) return;
          batch.set(doc(collection(db, 'inboxItems')), {
            userId: uid, type: 'notification', category: 'meeting', isRead: false,
            priority: 'medium', eventId: eventRef.id, entityId, clubName,
            title: `Meeting toegevoegd aan je agenda: ${form.title.trim()}`,
            message: `${formatEventDate(startTs)} — ${clubName}`,
            autoAdded: true,
            createdAt: serverTimestamp(),
          });
        });
      } else {
        // ── Uitnodiging: alleen planner krijgt het event; leden ontvangen RSVP-uitnodiging ──
        const eventData = clean({
          title: form.title.trim(),
          description: form.description.trim() || null,
          startTime: startTs,
          endTime: Timestamp.fromDate(endDateTime),
          type: form.type,
          category: 'work', domain: 'work',
          participants: [currentUserId],
          participantNames: { [currentUserId]: currentUserName },
          location: form.location.trim() || null,
          meetingUrl: resolvedMeetingUrl || null,
          status: 'scheduled',
          createdBy: currentUserId,
          createdAt: serverTimestamp(),
          color: form.color,
          reminderEnabled: form.reminderEnabled,
          reminderMinutesBefore: form.reminderMinutesBefore,
          reminderAt: reminderAt ? Timestamp.fromDate(reminderAt) : null,
          reminderSent: false,
          recurring: recurring || null,
          entityId,
          entityType: 'networkclub',
          visibility: form.visibility,
          source: 'superkonnected',
          host: hostData,
        });
        batch.set(eventRef, eventData);

        // RSVP-uitnodiging per lid — verschijnt onder tab "Uitnodigingen" in inbox
        memberUids.forEach((uid) => {
          if (uid === currentUserId) return;
          batch.set(doc(collection(db, 'inboxItems')), {
            userId: uid,
            type: 'meeting_invite',
            category: 'meeting',
            isRead: false,
            priority: 'high',
            eventId: eventRef.id,
            entityId,
            clubName,
            title: form.title.trim(),
            message: form.description.trim() || '',
            startTime: startTs,
            endTime: Timestamp.fromDate(endDateTime),
            location: form.location.trim() || null,
            meetingUrl: resolvedMeetingUrl || null,
            organizer: currentUserName,
            rsvpStatus: null,       // null = nog niet beantwoord
            requiresConsent: true,  // vlag voor InboxPage om RSVP-knoppen te tonen
            createdAt: serverTimestamp(),
          });
        });
      }

      // Herinneringen voor de planner zelf (altijd)
      if (form.reminderEnabled && reminderAt) {
        const reminderDelay = form.reminderMinutesBefore < 60
          ? `${form.reminderMinutesBefore} minuten`
          : `${form.reminderMinutesBefore / 60} uur`;
        batch.set(doc(collection(db, 'inboxItems')), {
          userId: currentUserId,
          type: 'reminder', category: 'meeting', isRead: false, priority: 'high',
          eventId: eventRef.id, entityId,
          clubName,
          title: `Herinnering: ${form.title.trim()}`,
          message: `Over ${reminderDelay} begint de meeting — ${clubName}`,
          reminderAt: Timestamp.fromDate(reminderAt),
          reminderSent: false,
          createdAt: serverTimestamp(),
        });
      }

      await batch.commit();

      const wasAutoAdd = form.autoAdd;
      setForm(EMPTY_FORM);
      setShowForm(false);
      setSaved(wasAutoAdd ? 'auto' : 'invite');
      setTimeout(() => setSaved(false), 4000);
    } catch (e: any) {
      console.error('[ClubMeetingPlanner] save error', e?.code, e?.message, e);
      const msg = e?.code === 'permission-denied'
        ? 'Geen schrijftoegang. Controleer of je organisator bent.'
        : e?.code === 'unavailable'
        ? 'Geen verbinding. Controleer je internetverbinding.'
        : `Opslaan mislukt: ${e?.message || 'Onbekende fout'}. Probeer opnieuw.`;
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const upcomingMeetings = meetings.filter(m => isUpcoming(m.startTime));
  const pastMeetings     = meetings.filter(m => !isUpcoming(m.startTime));

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Header + planknop */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>Meetings plannen</div>
          <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
            Verschijnt in de persoonlijke agenda van alle leden
          </div>
        </div>
        {canPlan && !showForm && (
          <button
            onClick={() => setShowForm(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 10, background: accentColor, color: '#fff', border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
          >
            + Meeting plannen
          </button>
        )}
      </div>

      {saved === 'invite' && (
        <div style={{ padding: '10px 16px', background: '#ECFDF5', borderRadius: 10, color: '#065F46', fontSize: 13, fontWeight: 600, border: '1px solid #A7F3D0' }}>
          ✓ Uitnodiging verstuurd — leden kunnen accepteren via hun inbox
        </div>
      )}
      {saved === 'auto' && (
        <div style={{ padding: '10px 16px', background: '#FFFBEB', borderRadius: 10, color: '#92400E', fontSize: 13, fontWeight: 600, border: '1px solid #FDE68A' }}>
          ✓ Meeting direct toegevoegd aan de agenda van alle leden
        </div>
      )}

      {/* ── Planformulier ─────────────────────────────────────────────── */}
      {showForm && (
        <div style={{ background: '#fff', borderRadius: 16, border: `1px solid ${primaryColor}22`, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: '20px 22px' }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: '#111827', marginBottom: 16 }}>Nieuwe meeting</div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>

            {/* Titel */}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ fontSize: 11.5, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4 }}>Titel *</label>
              <input
                value={form.title}
                onChange={e => set('title', e.target.value)}
                placeholder={titlePlaceholder}
                style={{ width: '100%', height: 40, padding: '0 12px', borderRadius: 9, border: `1.5px solid ${error && !form.title ? '#EF4444' : '#E5E7EB'}`, fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
              />
            </div>

            {/* Type */}
            <div>
              <label style={{ fontSize: 11.5, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4 }}>Type</label>
              <select
                value={form.type}
                onChange={e => set('type', e.target.value)}
                style={{ width: '100%', height: 40, padding: '0 10px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: 13, background: '#fff', outline: 'none' }}
              >
                {EVENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>

            {/* Zichtbaarheid */}
            <div>
              <label style={{ fontSize: 11.5, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4 }}>Zichtbaar voor</label>
              <select
                value={form.visibility}
                onChange={e => set('visibility', e.target.value)}
                style={{ width: '100%', height: 40, padding: '0 10px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: 13, background: '#fff', outline: 'none' }}
              >
                {VISIBILITY_OPTIONS.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
              </select>
            </div>

            {/* Datum */}
            <div>
              <label style={{ fontSize: 11.5, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4 }}>Datum *</label>
              <input
                type="date"
                value={form.startDate}
                min={new Date().toISOString().slice(0, 10)}
                onChange={e => set('startDate', e.target.value)}
                style={{ width: '100%', height: 40, padding: '0 12px', borderRadius: 9, border: `1.5px solid ${error && !form.startDate ? '#EF4444' : '#E5E7EB'}`, fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
              />
            </div>

            {/* Starttijd + eindtijd */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                <label style={{ fontSize: 11.5, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4 }}>Starttijd</label>
                <input
                  type="time"
                  value={form.startTime}
                  onChange={e => set('startTime', e.target.value)}
                  style={{ width: '100%', height: 40, padding: '0 10px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ fontSize: 11.5, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4 }}>Eindtijd</label>
                <input
                  type="time"
                  value={form.endTime}
                  onChange={e => set('endTime', e.target.value)}
                  style={{ width: '100%', height: 40, padding: '0 10px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
            </div>

            {/* Locatie — adres-autocomplete via OpenStreetMap */}
            <div style={{ position: 'relative' }}>
              <label style={{ fontSize: 11.5, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4 }}>Locatie</label>
              <input
                value={form.location}
                onChange={e => { set('location', e.target.value); searchLocation(e.target.value); }}
                placeholder="Zoek adres of plaatsnaam…"
                autoComplete="off"
                style={{ width: '100%', height: 40, padding: '0 12px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
              />
              {locationSearching && (
                <div style={{ position: 'absolute', right: 12, top: 32, fontSize: 11, color: '#9CA3AF' }}>Zoeken…</div>
              )}
              {locationSuggestions.length > 0 && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1.5px solid #E5E7EB', borderRadius: 9, boxShadow: '0 4px 16px rgba(0,0,0,0.1)', zIndex: 50, overflow: 'hidden', marginTop: 2 }}>
                  {locationSuggestions.map((s, i) => (
                    <button key={i} type="button"
                      onClick={() => { set('location', s.display_name); setLocationSuggestions([]); }}
                      style={{ width: '100%', textAlign: 'left', padding: '9px 12px', fontSize: 12.5, color: '#374151', border: 'none', background: 'none', cursor: 'pointer', borderBottom: i < locationSuggestions.length - 1 ? '1px solid #F3F4F6' : 'none' }}
                      onMouseEnter={e => (e.currentTarget.style.background = '#F9FAFB')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                    >
                      {s.display_name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Online via Jitsi of eigen link */}
            <div>
              <label style={{ fontSize: 11.5, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 6 }}>Online meeting</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12.5, color: '#374151', padding: '8px 12px', borderRadius: 8, border: `1.5px solid ${form.useJitsi ? accentColor : '#E5E7EB'}`, background: form.useJitsi ? `${accentColor}0F` : '#fff' }}>
                  <input type="radio" name="onlineType" checked={form.useJitsi === true} onChange={() => { set('useJitsi', true); set('meetingUrl', ''); }}
                    style={{ accentColor, width: 15, height: 15 }} />
                  <span style={{ fontWeight: 600 }}>SuperKonnected Video</span>
                  <span style={{ fontSize: 11, color: '#9CA3AF', marginLeft: 4 }}>— ingebouwde videovergadering</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12.5, color: '#374151', padding: '8px 12px', borderRadius: 8, border: `1.5px solid ${form.useJitsi === false && form.meetingUrl ? accentColor : '#E5E7EB'}`, background: '#fff' }}>
                  <input type="radio" name="onlineType" checked={form.useJitsi === false} onChange={() => set('useJitsi', false)}
                    style={{ accentColor, width: 15, height: 15 }} />
                  <span style={{ fontWeight: 600 }}>Eigen link</span>
                  <span style={{ fontSize: 11, color: '#9CA3AF', marginLeft: 4 }}>— Zoom, Teams, etc.</span>
                </label>
                {form.useJitsi === false && (
                  <input
                    value={form.meetingUrl}
                    onChange={e => set('meetingUrl', e.target.value)}
                    placeholder="https://..."
                    style={{ height: 38, padding: '0 12px', borderRadius: 8, border: '1.5px solid #E5E7EB', fontSize: 13, outline: 'none', marginTop: 2 }}
                  />
                )}
              </div>
            </div>

            {/* Herhaling */}
            <div>
              <label style={{ fontSize: 11.5, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4 }}>Herhaling</label>
              <select
                value={form.repeat}
                onChange={e => set('repeat', e.target.value)}
                style={{ width: '100%', height: 40, padding: '0 10px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: 13, background: '#fff', outline: 'none' }}
              >
                {REPEAT_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>

            {/* Einddatum herhaling */}
            <div>
              <label style={{ fontSize: 11.5, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4 }}>Einddatum herhaling</label>
              <input
                type="date"
                value={form.repeatEndDate}
                disabled={form.repeat === 'none'}
                onChange={e => set('repeatEndDate', e.target.value)}
                style={{ width: '100%', height: 40, padding: '0 12px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: 13, outline: 'none', opacity: form.repeat === 'none' ? 0.4 : 1, boxSizing: 'border-box' }}
              />
            </div>

            {/* Beschrijving */}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ fontSize: 11.5, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4 }}>Beschrijving</label>
              <textarea
                value={form.description}
                onChange={e => set('description', e.target.value)}
                rows={3}
                placeholder="Agenda, doel van de meeting…"
                style={{ width: '100%', padding: '10px 12px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: 13, outline: 'none', resize: 'none', boxSizing: 'border-box' }}
              />
            </div>

            {/* Herinnering */}
            <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151' }}>
                <input
                  type="checkbox"
                  checked={form.reminderEnabled}
                  onChange={e => set('reminderEnabled', e.target.checked)}
                  style={{ accentColor: accentColor, width: 16, height: 16, cursor: 'pointer' }}
                />
                Herinnering instellen
              </label>
              {form.reminderEnabled && (
                <select
                  value={form.reminderMinutesBefore}
                  onChange={e => set('reminderMinutesBefore', Number(e.target.value))}
                  style={{ height: 36, padding: '0 10px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: 12, background: '#fff', outline: 'none' }}
                >
                  {REMINDER_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              )}
            </div>

            {/* Toestemmingskeuze */}
            <div style={{ gridColumn: '1 / -1', borderRadius: 12, border: '1.5px solid #E5E7EB', overflow: 'hidden' }}>
              {/* Optie A: uitnodiging */}
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 16px', cursor: 'pointer', background: !form.autoAdd ? '#F0FDF4' : '#fff', borderBottom: '1px solid #E5E7EB' }}>
                <input type="radio" name="autoAdd" checked={!form.autoAdd} onChange={() => set('autoAdd', false)}
                  style={{ marginTop: 2, accentColor: '#10B981', width: 16, height: 16, flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>Uitnodiging sturen — leden kiezen zelf</div>
                  <div style={{ fontSize: 11.5, color: '#6B7280', marginTop: 2, lineHeight: 1.5 }}>
                    Leden ontvangen een RSVP-uitnodiging in hun inbox. Pas na &ldquo;Ja&rdquo; verschijnt het in hun persoonlijke agenda.
                  </div>
                </div>
              </label>
              {/* Optie B: automatisch */}
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 16px', cursor: 'pointer', background: form.autoAdd ? '#FFFBEB' : '#fff' }}>
                <input type="radio" name="autoAdd" checked={form.autoAdd} onChange={() => set('autoAdd', true)}
                  style={{ marginTop: 2, accentColor: '#F59E0B', width: 16, height: 16, flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>Verplicht — direct in agenda van alle leden</div>
                  <div style={{ fontSize: 11.5, color: '#6B7280', marginTop: 2, lineHeight: 1.5 }}>
                    Meeting wordt direct zichtbaar in ieders agenda. Gebruik dit alleen voor verplichte bijeenkomsten.
                  </div>
                </div>
              </label>
            </div>

            {/* Host / Gastspreker */}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ fontSize: 11.5, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 8 }}>Host / Gastspreker</label>
              {/* Type toggle */}
              <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                {([
                  { v: 'none',          label: 'Geen' },
                  { v: 'member',        label: 'Lid aanwijzen' },
                  { v: 'guest_speaker', label: 'Gastspreker' },
                ] as const).map(({ v, label }) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => { set('hostType', v); set('hostUid', ''); set('hostName', ''); set('hostPhotoURL', ''); set('guestSpeakerBio', ''); }}
                    style={{ height: 34, padding: '0 14px', borderRadius: 8, border: `1.5px solid ${form.hostType === v ? accentColor : '#E5E7EB'}`, background: form.hostType === v ? `${accentColor}12` : '#fff', fontSize: 12.5, fontWeight: form.hostType === v ? 700 : 500, color: form.hostType === v ? accentColor : '#6B7280', cursor: 'pointer' }}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* Member picker */}
              {form.hostType === 'member' && members.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {members.map((m) => {
                    const selected = form.hostUid === m.id;
                    return (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => { set('hostUid', m.id); set('hostName', m.name); set('hostPhotoURL', m.photoURL || ''); }}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', borderRadius: 10, border: `1.5px solid ${selected ? accentColor : '#E5E7EB'}`, background: selected ? `${accentColor}12` : '#F9FAFB', cursor: 'pointer' }}
                      >
                        {m.photoURL
                          ? <img src={m.photoURL} alt={m.name} style={{ width: 26, height: 26, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                          : <div style={{ width: 26, height: 26, borderRadius: '50%', background: accentColor, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, flexShrink: 0 }}>
                              {m.name.charAt(0).toUpperCase()}
                            </div>
                        }
                        <span style={{ fontSize: 12.5, fontWeight: selected ? 700 : 500, color: selected ? accentColor : '#374151' }}>{m.name}</span>
                        {selected && <span style={{ fontSize: 16, lineHeight: 1 }}>✓</span>}
                      </button>
                    );
                  })}
                </div>
              )}
              {form.hostType === 'member' && members.length === 0 && (
                <p style={{ fontSize: 12, color: '#9CA3AF' }}>Geen leden beschikbaar.</p>
              )}

              {/* Guest speaker fields */}
              {form.hostType === 'guest_speaker' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <input
                    value={form.hostName}
                    onChange={e => set('hostName', e.target.value)}
                    placeholder="Naam gastspreker *"
                    style={{ height: 40, padding: '0 12px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: 13, outline: 'none' }}
                  />
                  <input
                    value={form.hostPhotoURL}
                    onChange={e => set('hostPhotoURL', e.target.value)}
                    placeholder="Foto URL (optioneel)"
                    style={{ height: 40, padding: '0 12px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: 13, outline: 'none' }}
                  />
                  <textarea
                    value={form.guestSpeakerBio}
                    onChange={e => set('guestSpeakerBio', e.target.value)}
                    placeholder="Korte bio of onderwerp (optioneel)"
                    rows={2}
                    style={{ padding: '10px 12px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: 13, outline: 'none', resize: 'none' }}
                  />
                  {/* Preview */}
                  {(form.hostName || form.hostPhotoURL) && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#F9FAFB', borderRadius: 10, border: '1.5px solid #E5E7EB' }}>
                      {form.hostPhotoURL
                        ? <img src={form.hostPhotoURL} alt={form.hostName} style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                        : <div style={{ width: 36, height: 36, borderRadius: '50%', background: accentColor, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 800, flexShrink: 0 }}>
                            {(form.hostName || '?').charAt(0).toUpperCase()}
                          </div>
                      }
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{form.hostName || '—'}</div>
                        {form.guestSpeakerBio && <div style={{ fontSize: 11.5, color: '#6B7280', marginTop: 1 }}>{form.guestSpeakerBio}</div>}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Kleur */}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ fontSize: 11.5, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 6 }}>Kleur in agenda</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {COLORS.map(c => (
                  <button
                    key={c}
                    onClick={() => set('color', c)}
                    style={{ width: 28, height: 28, borderRadius: '50%', background: c, border: form.color === c ? `3px solid #111` : '3px solid transparent', cursor: 'pointer', flexShrink: 0 }}
                    aria-label={`Kleur ${c}`}
                  />
                ))}
              </div>
            </div>
          </div>

          {error && (
            <div role="alert" style={{ marginTop: 10, fontSize: 12.5, color: '#EF4444', fontWeight: 600 }}>{error}</div>
          )}

          {/* Actieknoppen */}
          <div style={{ display: 'flex', gap: 8, marginTop: 18, justifyContent: 'flex-end' }}>
            <button
              onClick={() => { setShowForm(false); setForm(EMPTY_FORM); setError(null); }}
              style={{ height: 40, padding: '0 18px', borderRadius: 10, border: '1.5px solid #E5E7EB', background: '#fff', fontSize: 13, color: '#6B7280', cursor: 'pointer', fontWeight: 600 }}
            >
              Annuleren
            </button>
            <button
              onClick={() => void handleSave()}
              disabled={saving}
              aria-busy={saving}
              style={{ height: 40, padding: '0 22px', borderRadius: 10, border: 'none', background: accentColor, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}
            >
              {saving ? 'Opslaan\u2026' : 'Meeting plannen'}
            </button>
          </div>
        </div>
      )}

      {/* ── Aankomende meetings ────────────────────────────────────────── */}
      {upcomingMeetings.length > 0 && (
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Aankomend</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {upcomingMeetings.map(m => (
              <MeetingCard key={m.id} meeting={m} primaryColor={primaryColor} accentColor={accentColor} />
            ))}
          </div>
        </div>
      )}

      {/* ── Verleden meetings ──────────────────────────────────────────── */}
      {pastMeetings.length > 0 && (
        <details style={{ marginTop: 4 }}>
          <summary style={{ fontSize: 12, fontWeight: 700, color: '#9CA3AF', cursor: 'pointer', userSelect: 'none', paddingBottom: 8 }}>
            Eerdere meetings ({pastMeetings.length})
          </summary>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
            {pastMeetings.slice().reverse().map(m => (
              <MeetingCard key={m.id} meeting={m} primaryColor={primaryColor} accentColor={accentColor} past />
            ))}
          </div>
        </details>
      )}

      {meetings.length === 0 && !showForm && (
        <div style={{ textAlign: 'center', padding: '32px 24px', background: '#fff', borderRadius: 14, border: `1.5px dashed ${primaryColor}33` }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>&#x1F4C5;</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#374151', marginBottom: 4 }}>Geen meetings gepland</div>
          <div style={{ fontSize: 12.5, color: '#9CA3AF' }}>
            {canPlan ? 'Klik op "Meeting plannen" om een bijeenkomst in te plannen.' : 'Er zijn nog geen meetings gepland voor deze club.'}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Meeting kaart ─────────────────────────────────────────────────────────

const MeetingCard: React.FC<{
  meeting: SharedAgendaEvent;
  primaryColor: string;
  accentColor: string;
  past?: boolean;
}> = ({ meeting: m, primaryColor, accentColor, past = false }) => {
  const start = m.startTime instanceof Date ? m.startTime : (m.startTime as any)?.toDate?.() ?? new Date();
  const end   = m.endTime   instanceof Date ? m.endTime   : (m.endTime as any)?.toDate?.()   ?? new Date();
  const hasReminder = m.reminderEnabled && !m.reminderSent;
  const host = (m as any).host as { type: string; uid?: string; name?: string; photoURL?: string; bio?: string } | null | undefined;

  return (
    <div style={{
      display: 'flex', gap: 12, padding: '12px 16px',
      background: past ? '#F9FAFB' : '#fff',
      borderRadius: 12,
      border: `1.5px solid ${past ? '#F3F4F6' : (m.color || primaryColor) + '33'}`,
      borderLeft: `4px solid ${past ? '#D1D5DB' : (m.color || accentColor)}`,
      opacity: past ? 0.75 : 1,
    }}>
      {/* Datum kolom */}
      <div style={{ minWidth: 48, textAlign: 'center' }}>
        <div style={{ fontSize: 20, fontWeight: 900, color: past ? '#9CA3AF' : primaryColor, lineHeight: 1 }}>
          {start.getDate()}
        </div>
        <div style={{ fontSize: 11, color: '#9CA3AF', textTransform: 'uppercase', fontWeight: 700 }}>
          {start.toLocaleDateString('nl-NL', { month: 'short' })}
        </div>
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: past ? '#6B7280' : '#111827', marginBottom: 2 }}>{m.title}</div>
        <div style={{ fontSize: 12, color: '#6B7280' }}>
          {start.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
          {' '}&ndash;{' '}
          {end.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
          {m.location && <span> &middot; {m.location}</span>}
        </div>
        {m.description && (
          <div style={{ fontSize: 11.5, color: '#9CA3AF', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {m.description}
          </div>
        )}

        {/* Host / Gastspreker */}
        {host && (host.name || host.photoURL) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 8, padding: '6px 10px', borderRadius: 8, background: '#F9FAFB', border: '1px solid #F3F4F6', width: 'fit-content' }}>
            {host.photoURL
              ? <img src={host.photoURL} alt={host.name || ''} style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
              : <div style={{ width: 24, height: 24, borderRadius: '50%', background: accentColor, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, flexShrink: 0 }}>
                  {(host.name || '?').charAt(0).toUpperCase()}
                </div>
            }
            <div>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: '#374151' }}>{host.name}</div>
              <div style={{ fontSize: 10, color: '#9CA3AF' }}>
                {host.type === 'guest_speaker' ? 'Gastspreker' : 'Host'}
                {host.bio && <span> · {host.bio}</span>}
              </div>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 6, marginTop: 7, flexWrap: 'wrap' }}>
          {/* Type badge */}
          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: `${m.color || accentColor}18`, color: m.color || accentColor, border: `1px solid ${m.color || accentColor}33` }}>
            {EVENT_TYPES.find(t => t.value === m.type)?.label ?? m.type}
          </span>
          {hasReminder && (
            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: '#FEF3C7', color: '#92400E', border: '1px solid #FDE68A' }}>
              &#x23F0; Herinnering actief
            </span>
          )}
          {m.recurring && (
            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: '#EDE9FE', color: '#5B21B6', border: '1px solid #DDD6FE' }}>
              &#x1F501; {REPEAT_OPTIONS.find(r => r.value === m.recurring?.frequency)?.label ?? 'Herhalend'}
            </span>
          )}
          {m.meetingUrl && (
            <a href={m.meetingUrl} target="_blank" rel="noopener noreferrer"
              style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: '#DBEAFE', color: '#1E40AF', border: '1px solid #BFDBFE', textDecoration: 'none' }}>
              Online link &#x2197;
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClubMeetingPlanner;
